import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MaskLine, EASE } from "../components/motion";
import { scrollToHash } from "../components/Nav";

const HERO_IMG = "https://images.unsplash.com/photo-1457195740896-7f345efef228?q=80&w=2400&auto=format&fit=crop";

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.16]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero"
      className="grain relative flex min-h-[100svh] flex-col justify-end overflow-hidden bg-navy text-cream"
    >
      <motion.div style={{ y: bgY, scale: bgScale }} className="absolute inset-0">
        <img
          src={HERO_IMG}
          alt="A calm open ocean horizon at first light"
          className="h-full w-full object-cover"
          loading="eager"
          data-testid="hero-image"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-b from-navy/75 via-navy/35 to-navy" />
      <div className="absolute inset-0 bg-gradient-to-r from-navy/60 via-transparent to-navy/25" />

      <motion.div style={{ opacity: fade }} className="relative z-10 mx-auto w-full max-w-7xl px-5 pb-20 pt-36 sm:px-8 sm:pb-24">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.15, ease: EASE }}
          className="mb-8 inline-flex items-center gap-3 border border-gold/40 bg-navy-abyss/50 px-4 py-2 backdrop-blur-sm"
          data-testid="hero-status"
        >
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-gold-light opacity-70" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-gold-light" />
          </span>
          <span className="font-mono text-[10px] uppercase tracking-[0.24em] text-gold-light sm:text-[11px]">
            Our inaugural retreat is in development.
          </span>
        </motion.div>

        <h1
          className="font-serif text-[2.7rem] font-light leading-[1.06] tracking-tight sm:text-6xl lg:text-7xl xl:text-[5.4rem]"
          data-testid="hero-heading"
        >
          <MaskLine delay={0.3} duration={1.1}>
            Care for the <em className="italic text-gold-light">One</em>
          </MaskLine>
          <MaskLine delay={0.44} duration={1.1}>
            Who Cares for <em className="italic text-gold-light">Others</em>.
          </MaskLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.85, ease: EASE }}
          className="mt-7 max-w-xl text-base font-light leading-relaxed text-cream/85 sm:text-lg"
          data-testid="hero-subtext"
        >
          Cruise retreats for chaplains—and those exploring a calling to chaplaincy—seeking rest,
          spiritual renewal, creative expression, and meaningful connection.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.0, ease: EASE }}
          className="mt-8 flex items-center gap-4"
          data-testid="hero-tagline"
        >
          <span className="h-px w-10 bg-gold" />
          <span className="font-mono text-[11px] uppercase tracking-[0.34em] text-gold-light sm:text-xs">
            Rest. Renew. Write. Connect.
          </span>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.15, ease: EASE }}
          className="mt-10 flex flex-col gap-4 sm:flex-row sm:items-center"
        >
          <a
            href="#interest"
            data-testid="hero-cta-join"
            onClick={(e) => {
              e.preventDefault();
              scrollToHash("#interest");
            }}
            className="inline-flex items-center justify-center bg-gold px-8 py-4 font-mono text-[12px] uppercase tracking-[0.2em] text-navy transition-all duration-300 hover:bg-gold-light hover:shadow-[0_8px_30px_rgba(184,144,71,0.35)]"
          >
            Join the Interest List
          </a>
          <a
            href="#experience"
            data-testid="hero-cta-explore"
            onClick={(e) => {
              e.preventDefault();
              scrollToHash("#experience");
            }}
            className="inline-flex items-center justify-center border border-cream/40 px-8 py-4 font-mono text-[12px] uppercase tracking-[0.2em] text-cream transition-all duration-300 hover:border-gold-light hover:text-gold-light"
          >
            Explore the Experience
          </a>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="absolute bottom-8 right-6 z-10 hidden items-center gap-3 sm:flex sm:right-10"
        aria-hidden="true"
      >
        <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-cream/50">Scroll</span>
        <motion.span
          animate={{ scaleY: [1, 0.4, 1], opacity: [0.6, 1, 0.6] }}
          transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
          className="block h-10 w-px origin-top bg-gold-light/70"
        />
      </motion.div>
    </section>
  );
}
